import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@store'
import toast from 'react-hot-toast'
import apiClient from '@services/api/client'

function FreeTrialDurationPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()
  const { sessionToken, postData, postType } = location.state || {}
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [freeTrialInfo, setFreeTrialInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  
  // Durée fixe de 7 jours pour la période d'essai
  const FIXED_DURATION = 7

  useEffect(() => {
    // Vérifier si l'utilisateur est en période d'essai
    checkFreeTrial()
  }, [])

  const checkFreeTrial = async () => {
    try {
      const clientId = user?.client_id || user?.id
      if (!clientId) {
        toast.error('Erreur: Utilisateur non connecté')
        navigate('/login')
        return
      }

      const r = await apiClient.get('/subscription/status')
      if (!r.isFreeTrialActive) {
        // Période d'essai expirée, rediriger vers la page de paiement
        toast.error('Votre période d\'essai gratuit est expirée')
        navigate('/payment-article', { state: location.state })
        return
      }
      setFreeTrialInfo(r)
    } catch (error) {
      console.error('Erreur lors de la vérification de la période d\'essai:', error)
      toast.error('Erreur lors de la vérification de votre compte')
      navigate('/dashboard')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!sessionToken) {
      toast.error('Session invalide')
      return
    }

    setIsSubmitting(true)

    try {
      await apiClient.post('/subscription/confirm', {
        session_token: sessionToken,
        payment_method: 'free_trial',
        duration: FIXED_DURATION
      })
      toast.success('🎉 Votre annonce a été publiée avec succès!')
      navigate('/dashboard')
    } catch (error) {
      console.error('Erreur:', error)
      toast.error(error.message || 'Erreur lors de la publication')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto"></div>
          <p className="mt-4 text-gray-600">Vérification de votre compte...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* En-tête */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Période d'essai gratuit 🎉
          </h1>
          <p className="text-gray-600">
            Publiez votre annonce gratuitement pendant votre période d'essai
          </p>
          {freeTrialInfo && (
            <div className="mt-4 inline-block bg-green-50 border border-green-200 rounded-lg px-4 py-2">
              <p className="text-sm text-green-800">
                <span className="font-semibold">{freeTrialInfo.daysLeft} jours restants</span> dans votre période d'essai
              </p>
            </div>
          )}
        </div>

        {/* Carte principale */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Publication gratuite - 7 jours
          </h2>

          {/* Affichage de la durée fixe */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-green-100 rounded-full p-3">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">7 jours d'affichage</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Durée standard pour la période d'essai gratuit
                  </p>
                </div>
              </div>
              <div className="text-right">
                <span className="text-3xl font-bold text-green-600">GRATUIT</span>
                <p className="text-sm text-gray-500 mt-1">0 F CFA</p>
              </div>
            </div>
          </div>

          {/* Informations */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-900">
                  Avantage période d'essai
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  Profitez de publications gratuites pendant toute votre période d'essai. 
                  Aucune carte bancaire requise.
                </p>
              </div>
            </div>
          </div>

          {/* Bouton de confirmation */}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full mt-6 bg-[#D4AF37] hover:bg-[#B8941F] text-white font-semibold py-4 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Publication en cours...
              </>
            ) : (
              <>
                Publier gratuitement
                <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </>
            )}
          </button>
        </div>

        {/* Bouton retour */}
        <button
          onClick={() => navigate('/dashboard')}
          className="w-full text-gray-600 hover:text-gray-900 font-medium py-3 transition-colors"
        >
          ← Retour au tableau de bord
        </button>
      </div>
    </div>
  )
}

export default FreeTrialDurationPage
